import type { Ssm5RecentConnection } from '@packages/domain/ssm5';
import type { EnrichedVehicleDetails } from '@web/vin-sessions/types';

import { Button } from '@packages/ui/shared';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';
import { Ssm5ConnectionBadge } from './ssm5-connection-badge';
import { Ssm5NotificationBox } from './ssm5-notification-box';

const CONNECTED_AT_FORMAT = 'MMM d, yyyy HH:mm';
const VIN_TAIL_LENGTH = 8;

/** Props for {@link Ssm5RecentConnectionCard}. */
export type Ssm5RecentConnectionCardProps = {
    /** Whether the connection is still open on its Cloud Connector. */
    isConnected: boolean;
    /** Receives the connection's Cloud Connector ID when Open is pressed. */
    onOpen: (cloudConnectorId: string) => void;
    /** The connection the card presents. */
    recentConnection: Ssm5RecentConnection;
    /** The vehicle of the open VIN session the connection belongs to. */
    vehicle: EnrichedVehicleDetails;
};

/**
 * The recent connection of the open VIN session: the vehicle it ran on, its Cloud Connector ID
 * and time, whether it is still open, and the Open action that reconnects through it.
 *
 * @param props - {@link Ssm5RecentConnectionCardProps}
 * @returns the card
 */
export function Ssm5RecentConnectionCard({
    isConnected,
    onOpen,
    recentConnection,
    vehicle,
}: Ssm5RecentConnectionCardProps) {
    const { t } = useTranslation();
    const { model, modelYear, vin } = vehicle.vinRecord;

    const open = useCallback(
        () => onOpen(recentConnection.cloudConnectorId),
        [onOpen, recentConnection.cloudConnectorId]
    );

    return (
        <Ssm5NotificationBox
            action={
                <Button
                    onPress={open}
                    style={ssm5ControlStyles.smallButton}
                    title={t('ssm5:recent.open')}
                    titleStyle={ssm5TextStyles.primaryButtonTitle}
                    variant="ghost-blue"
                />
            }
            badge={<Ssm5ConnectionBadge isConnected={isConnected} />}
            body={t('ssm5:recent.description', {
                cloudConnectorId: recentConnection.cloudConnectorId,
                connectedAt: format(recentConnection.connectedAt, CONNECTED_AT_FORMAT),
            })}
            heading={`${model} ‘${String(modelYear).slice(-2)} - ${vin.slice(-VIN_TAIL_LENGTH)}`}
        />
    );
}
